import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Divider,
  Grid,
  Typography,
} from '@mui/material';
import { Container } from '@mui/system';
import axios from 'axios';
import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { url } from 'src/Helpers/utils/Constant';
import { ISingleDuplicateCertificateApplication } from 'src/Types/module/applications/applications';
import { getStatusLabel } from 'src/Utils/Combined';
import PdfAndImageView from 'src/components/pdfAndImageView/PdfAndImageView';
import { CommonTypoGraphy } from 'src/components/pdfAndImageView/CommonTypoGraphy';
import OfflinePaymentCardInformation from '../../subModules/invoice/singleInvoice/OfflinePaymentCardInformation';
import ModalsWrapper from 'src/components/Modals/ModalsWrapper';
import UpdateDuplicateCertificateApp from './UpdateDuplicateCertificateApp';
import CommonDetailItem, {
  formatDate,
  formatValue,
} from 'src/components/CommonDetailItem/CommonDetailItem';

const labelWidth = { xs: '130px', sm: '150px', md: '170px' };

const SingleDuplicateCertificateApplication = () => {
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const { id } = useParams();
  const [application, setApplication] =
    useState<ISingleDuplicateCertificateApplication | null>(null);
  const navigate = useNavigate();
  const [imageViewOpen, SetImageViewOpen] = useState<boolean>(false);
  const [imageFile, setImageFile] = useState<string | null>('');

  useEffect(() => {
    (async () => {
      try {
        const data = await axios.get(
          `${url}/api/member/duplicate-certificate-application/${id}`
        );
        if (data.data.success) {
          setApplication(data.data.data);
        }
      } catch (err) {
        alert('No application found');
        navigate('/update-applications/duplicate-certificate');
      }
    })();
  }, [id]);

  const applicationDate = formatDate(
    application?.duplicate_certificate_application_created_at,
    'MMM Do YYYY'
  );

  const statusLabel = application?.duplicate_certificate_application_status
    ? getStatusLabel(application.duplicate_certificate_application_status)
    : 'N/A';

  const civilAviationFile = application?.duplicate_certificate_application_civil_aviation ? (
    <CommonTypoGraphy
      name='View Civil Aviation'
      onClick={() => {
        SetImageViewOpen(true);
        setImageFile(
          application.duplicate_certificate_application_civil_aviation
        );
      }}
    />
  ) : (
    'N/A'
  );

  const tradeLicenseFile = application?.duplicate_certificate_application_trade_license ? (
    <CommonTypoGraphy
      name='View Trade License'
      onClick={() => {
        SetImageViewOpen(true);
        setImageFile(
          application.duplicate_certificate_application_trade_license
        );
      }}
    />
  ) : (
    'N/A'
  );

  const gdCopyFile = application?.duplicate_certificate_application_gd_copy ? (
    <CommonTypoGraphy
      name='View Duplicate Certificate Copy'
      onClick={() => {
        SetImageViewOpen(true);
        setImageFile(application.duplicate_certificate_application_gd_copy);
      }}
    />
  ) : (
    'N/A'
  );

  const taxReturnFile = application?.duplicate_certificate_application_tax_return ? (
    <CommonTypoGraphy
      name='View Tax Return File'
      onClick={() => {
        SetImageViewOpen(true);
        setImageFile(
          application.duplicate_certificate_application_tax_return
        );
      }}
    />
  ) : (
    'N/A'
  );

  const applicationFile = application?.duplicate_certificate_application_application_file ? (
    <CommonTypoGraphy
      name='View Application'
      onClick={() => {
        SetImageViewOpen(true);
        setImageFile(
          application.duplicate_certificate_application_application_file
        );
      }}
    />
  ) : (
    'N/A'
  );

  return (
    <>
      <Helmet>
        <title>Atab - Duplicate certificate application</title>
      </Helmet>
      <Container maxWidth='xl'>
        <Card>
          <CardHeader
            title='Duplicate certificate application'
            action={
              <Button onClick={() => setShowModal(true)} variant='outlined'>
                Update
              </Button>
            }
          />
          <Divider />
          <CardContent>
            {application ? (
              <Grid container spacing={2} pt={2}>
                <CommonDetailItem
                  label='Application Date'
                  value={applicationDate}
                  labelWidth={labelWidth}
                  xs={12}
                  sm={6}
                  md={4}
                />
                <CommonDetailItem
                  label='Company name'
                  value={formatValue(application.user_member_company_name)}
                  labelWidth={labelWidth}
                  xs={12}
                  sm={6}
                  md={4}
                />
                <CommonDetailItem
                  label='Status'
                  value={statusLabel}
                  labelWidth={labelWidth}
                  xs={12}
                  sm={6}
                  md={4}
                />
                <CommonDetailItem
                  label='Civil Aviation File'
                  value={civilAviationFile}
                  labelWidth={labelWidth}
                  xs={12}
                  sm={6}
                  md={4}
                />
                <CommonDetailItem
                  label='Trade License File'
                  value={tradeLicenseFile}
                  labelWidth={labelWidth}
                  xs={12}
                  sm={6}
                  md={4}
                />
                <CommonDetailItem
                  label='Duplicate Cert Copy'
                  value={gdCopyFile}
                  labelWidth={labelWidth}
                  xs={12}
                  sm={6}
                  md={4}
                />
                <CommonDetailItem
                  label='Tax Return File'
                  value={taxReturnFile}
                  labelWidth={labelWidth}
                  xs={12}
                  sm={6}
                  md={4}
                />
                <CommonDetailItem
                  label='Application File'
                  value={applicationFile}
                  labelWidth={labelWidth}
                  xs={12}
                  sm={6}
                  md={4}
                />
                <CommonDetailItem
                  label='Reject Reason'
                  value={formatValue(
                    application.duplicate_certificate_application_rejeceted_reason
                  )}
                  labelWidth={labelWidth}
                  xs={12}
                  sm={12}
                  md={12}
                />

                {application?.paymentInfo && (
                  <Grid item xs={12} mt={2}>
                    <Divider
                      sx={{
                        position: 'relative',
                        justifyContent: 'space-between',
                      }}
                    >
                      <Chip
                        label='Payment Information'
                        size='small'
                        color='primary'
                      />
                      <Link
                        to={`/invoices/transactions/${application?.paymentInfo?.invoice_id}`}
                      >
                        <Chip
                          label='Go Invoice'
                          size='small'
                          sx={{
                            position: 'absolute',
                            right: 0,
                            background: '#D7E9FD',
                          }}
                        />
                      </Link>
                    </Divider>

                    <OfflinePaymentCardInformation
                      data={application?.paymentInfo}
                    />
                  </Grid>
                )}
              </Grid>
            ) : (
              <Typography
                variant='body1'
                fontWeight='bold'
                color='text.primary'
                display='inline'
                sx={{ pr: 1 }}
                gutterBottom
              >
                No Data found
              </Typography>
            )}
          </CardContent>
        </Card>
      </Container>

      {application && (
        <ModalsWrapper
          setShowModal={setShowModal}
          showModal={showModal}
          modalData={{
            title: 'Update Duplicate Certificate application',
          }}
        >
          <Box p={2}>
            <UpdateDuplicateCertificateApp
              application={application}
              setApplication={setApplication}
              setShowModal={setShowModal}
            />
          </Box>
        </ModalsWrapper>
      )}

      <PdfAndImageView
        setOpen={SetImageViewOpen}
        open={imageViewOpen}
        file={imageFile}
      />
    </>
  );
};

export default SingleDuplicateCertificateApplication;
